import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import useBoardFormAction, {
  useBoardFormActionProps,
} from '@/hooks/board/useBoardFormAction';
import BoardForm, {
  BoardFormProps,
} from '@/components/Board/CreateBoardModal/BoardForm';
import { BoardFormRef } from '@/components/Board/CreateBoardModal';
import useBoardForm from '@/hooks/board/useBoardForm';
import FormProvider from '@/components/HookForm/FormProvider';
import { stripHTML } from '@/utils';
import { useWatch } from 'react-hook-form';

type EditBoardFormProps = {
  documentId: string;
  isFormDirty: boolean;
  setIsFormDirty: (isFormDirty: boolean) => void;
  setDisabledSubmit: (value: boolean) => void;
  setDescription: (value: string) => void;
  setFormType: (formType: 'editor' | 'codeUpload') => void;
  handleClose: () => void;
} & Pick<useBoardFormActionProps, 'onSubmit'> &
  Pick<BoardFormProps, 'options'>;

const EditBoardForm = forwardRef<BoardFormRef, EditBoardFormProps>(
  (props, ref) => {
    const {
      documentId,
      setIsFormDirty,
      setDisabledSubmit,
      setFormType,
      options,
      onSubmit,
      handleClose,
    } = props;

    const methods = useBoardForm();
    const {
      control,
      getValues,
      setValue,
      formState: { isSubmitting, isValid, isSubmitSuccessful, dirtyFields },
    } = methods;
    const watchedContent = useWatch({ control, name: 'content' });
    const watchedFormType = useWatch({ control, name: 'formType' });

    const action = useBoardFormAction({
      documentId,
      methods,
      onSubmit,
    });
    const { isGetEditDetailBoardLoading } = action;

    // form submit Disabled 처리
    useEffect(() => {
      setDisabledSubmit(
        isGetEditDetailBoardLoading || !isValid || isSubmitting,
      );
    }, [isGetEditDetailBoardLoading, isValid, isSubmitting]);

    // form에 값 수정될 경우 isFormDirty 값 변경
    useEffect(() => {
      setIsFormDirty(
        Boolean(
          Object.keys(dirtyFields).some(
            (fieldName: string) =>
              fieldName !== 'content' ||
              !!stripHTML(watchedContent ?? '').trim(),
          ),
        ),
      );
    }, [dirtyFields, watchedContent]);

    // submit 완료 후 모달 닫기
    useEffect(() => {
      if (isSubmitSuccessful) handleClose();
    }, [isSubmitSuccessful]);

    // formType 변경 시 부모에게 알림
    useEffect(() => {
      setFormType(watchedFormType);
    }, [watchedFormType]);

    useImperativeHandle(ref, () => ({
      action: {
        ...action,
        getCodeZipFileStatus: () => {
          const values = getValues();
          return values.codeZipFile?.status;
        },
        getValues,
        setValue,
      },
    }));

    return (
      <FormProvider
        methods={methods}
        id={`CreateBoardModal-Edit-${documentId}`}
      >
        <BoardForm
          isLoading={isGetEditDetailBoardLoading}
          options={options}
          methods={methods}
        />
      </FormProvider>
    );
  },
);
EditBoardForm.displayName = 'EditBoardForm';

export default EditBoardForm;
